import * as THREE from 'three'
import { MarkerModel } from './marker'
import type { MarkerType } from 'Common/types/elements/visualizer/marker'
import type { Group, Scene } from 'three'
import type { InteractionManager } from 'three.interactive'
import type { MarkerEventCallback } from 'Common/types/elements/visualizer/visualizerTypes'

interface Markers {
    markerList: Array<MarkerType>
    markerModelList: Array<MarkerModel>
    markerEventCallback: MarkerEventCallback
    selectedMarker?: MarkerType
    selectedMarkerModel: MarkerModel
    interactionManager: InteractionManager
    showAllMarkers: boolean
    group: Group
    root: Group | Scene
}

class Markers implements Markers {
    constructor(
        markerList: Array<MarkerType>,
        markerEventCallback: MarkerEventCallback,
        interactionManager: InteractionManager,
        root: Group | Scene,
        selectedMarker: MarkerType,
        showAllMarkers = false,
        group?: THREE.Group
    ) {
        this.markerList = markerList || []
        this.markerEventCallback = markerEventCallback
        this.selectedMarker = selectedMarker
        this.selectedMarkerModel
        this.interactionManager = interactionManager
        this.root = root
        this.showAllMarkers = showAllMarkers
        this.group = group
        this.populate(this.group.name)
    }

    getMarkerByName(name: string) {
        return this.markerList.find((wp) => wp.name == name)
    }
    /**
     * Populates markers
     */
    populate(groupName = 'markers') {
        const existingMarkerGroup = this.root.getObjectByName(groupName)
        if (existingMarkerGroup !== undefined) {
            existingMarkerGroup.children.forEach((child) => {
                this.interactionManager.remove(child)
            })
            this.root.remove(existingMarkerGroup)
        }

        this.group = new THREE.Group()
        this.group.name = groupName
        this.group.position.set(0, 0, 0)
        this.group.rotation.set(0, 0, 0)
        this.group.scale.set(1, 1, 1)

        if (this.showAllMarkers) {
            this.populateAllMarkers()
        }
        this.root.add(this.group)
    }

    /**
     * Populates all marker based on the given marker data
     */
    populateAllMarkers() {
        this.markerList.forEach((marker: MarkerType) => {
            const markerModel = this.createMarker(marker)
            this.group.add(markerModel.mesh)
        })
    }

    /**
     * Creates and returns a single marker model by passing through the given marker information
     * @param wpInfo MarkerType Object which provides marker positioning and rotation information
     */
    createMarker(marker: MarkerType): MarkerModel {
        const markerModel = new MarkerModel(marker, this.markerEventCallback, this.interactionManager)
        return markerModel
    }

    /**
     * Sets the value of showAllMarkers
     * @param value Boolean value to set showAllMarker to
     */
    setShowAllMarkers(value: boolean) {
        this.showAllMarkers = value
        this.populate()
    }

    /**
     * Sets up a single marker model
     * @param marker markerType Object to set selected marker to
     */
    setSingleSelectedMarker(marker: MarkerType) {
        this.selectedMarker = marker
        this.populate()
    }

}

export { Markers }
